import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Gift, MessagesSquare } from 'lucide-react';
import {
  describeError,
  describeFieldError,
  getTenantAiUnits,
  grantTenantAiUnits,
  grantTenantAiUnitsUrl,
  isRouteMissing,
  problemErrorCode,
  problemFieldErrors,
  problemStatus,
  refundRefusalOf,
  refundTenantAiUnitPurchase,
  refundTenantAiUnitPurchaseUrl,
  revokeTenantAiUnitsGrant,
  revokeTenantAiUnitsGrantUrl,
  ROUTE_MISSING_MESSAGE,
  type GrantAiUnitsPayload,
  type PlatformAiUnitLot,
  type PlatformAiUnitPurchase,
  type PlatformAiUnitsResponse,
} from '@/lib/api';
import {
  balanceStateBadge,
  formatBrl,
  formatDate,
  formatDateTime,
  formatUnits,
  isRevocableConcession,
  lotSituation,
  lotSourceLabel,
  parseWholeNumber,
  paymentMethodLabel,
  purchaseStatusBadge,
  refundRefusalLabel,
  refundSummary,
  signedUnits,
  unitsLabel,
} from '@/lib/ai-units';
import { overrideEndsAtFor, overrideLastDay, saoPauloIsoDate } from '@/lib/plan-override';
import { operatorLabel } from '@/lib/printing';
import { useIdempotencyKey } from '@/lib/idempotency';
import { stepUpOptions, useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import { useAuthStore } from '@/stores/auth';
import { Badge } from './ui';
import { CardTable, THead, TBody, Tr, Th, Td } from './CardTable';
import { CONFIRM_REASON_MAX, CONFIRM_REASON_MIN, ReinforcedConfirmDialog } from './ReinforcedConfirmDialog';
import { StepUpDialog } from './StepUpDialog';

/**
 * Seção "Atendimentos" do detalhe do tenant (change billing-ai-units, decisão 27; requisito
 * "Atendimentos da loja no detalhe do tenant" de `platform-backoffice`), no molde da
 * `TenantPrintingSection`.
 *
 * A carteira do Atendente IA como o suporte precisa dela para responder ao dono: o saldo (ciclo,
 * pacotes e cortesias, carência), os lotes vigentes e fechados, os últimos 100 lançamentos e as
 * compras com a elegibilidade ao reembolso de 7 dias, em que a carência abatida do pacote conta
 * como uso. A API não devolve conteúdo nem identificador de conversa, e esta seção não os pede.
 *
 * Quem vê: `Commercial` e `Admin` (a rota é `RequirePlatformCommercial`); para o suporte a seção
 * não aparece. Conceder cortesia é dos dois; estornar cortesia e reembolsar pacote são só de
 * `Admin`, com motivo e confirmação reforçada. As rotas decidem por papel, e a tela também.
 *
 * Sem o plano com Atendente IA, a carteira continua respondida, e a seção avisa que os
 * atendimentos ficam parados, e invisíveis ao dono no painel, até haver plano.
 */

const COMMERCIAL_ROLES = new Set(['Commercial', 'Admin']);

/** Os mesmos limites da cortesia na API. */
const GRANT_MIN_UNITS = 1;
const GRANT_MAX_UNITS = 10_000;
/** Vale até o dia de hoje + 364 (inclusive): o fim exclusivo fica dentro dos 365 dias da API. */
const GRANT_MAX_DAYS = 364;

export function TenantAiUnitsSection({
  tenantId,
  tenantActive,
  tradeName,
}: {
  tenantId: string;
  tenantActive: boolean;
  tradeName: string;
}) {
  const role = useAuthStore((s) => s.operator?.role ?? null);
  if (!role || !COMMERCIAL_ROLES.has(role)) return null;
  return (
    <TenantAiUnitsSectionContent
      tenantId={tenantId}
      tenantActive={tenantActive}
      tradeName={tradeName}
      isAdmin={role === 'Admin'}
    />
  );
}

type Notice = { text: string; tone: 'info' | 'warning' | 'error' };

function TenantAiUnitsSectionContent({
  tenantId,
  tenantActive,
  tradeName,
  isAdmin,
}: {
  tenantId: string;
  tenantActive: boolean;
  tradeName: string;
  isAdmin: boolean;
}) {
  const currentOperatorId = useAuthStore((s) => s.operator?.id ?? null);
  const [data, setData] = useState<PlatformAiUnitsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [revoking, setRevoking] = useState<PlatformAiUnitLot | null>(null);
  const [refunding, setRefunding] = useState<PlatformAiUnitPurchase | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    // Tenant desativado: a rota responde 422, como a impressão. Nem pede.
    if (!tenantActive) return;
    let cancelled = false;
    getTenantAiUnits(tenantId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err) => !cancelled && setError(isRouteMissing(err) ? ROUTE_MISSING_MESSAGE : describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [tenantId, tenantActive, reloadTick]);

  const state = data ? balanceStateBadge(data.state) : null;

  return (
    <section className="card" aria-labelledby="tenant-ai-units-title">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <MessagesSquare size={18} />
        <div className="card-title" id="tenant-ai-units-title">
          Atendimentos
        </div>
        {state && (
          <Badge tone={state.tone} dot>
            {state.label}
          </Badge>
        )}
        {data && !data.hasAttendantPlan && <Badge tone="warning">Sem o plano do Atendente IA</Badge>}
      </div>

      {!tenantActive ? (
        <p className="muted" style={{ margin: 0 }}>
          Tenant desativado: os atendimentos não são consultados enquanto ele estiver inativo.
        </p>
      ) : error && !data ? (
        <div className="error-banner" role="alert">
          {error}
        </div>
      ) : !data ? (
        <p className="muted" style={{ margin: 0 }}>
          Carregando…
        </p>
      ) : (
        <>
          {notice && (
            <div
              className={notice.tone === 'error' ? 'error-banner' : notice.tone === 'warning' ? 'warning-banner' : 'info-banner'}
              role={notice.tone === 'error' ? 'alert' : 'status'}
              style={{ marginBottom: 12 }}
              data-testid="ai-units-notice"
            >
              {notice.text}
            </div>
          )}

          {!data.hasAttendantPlan && (
            <div className="warning-banner" style={{ marginBottom: 12 }} data-testid="ai-units-without-plan">
              <strong>Este tenant não tem o plano com Atendente IA.</strong> Os atendimentos da carteira ficam parados,
              e invisíveis ao dono no painel, até haver plano. A cortesia pode ser concedida mesmo assim e passa a valer
              quando o plano voltar, se ainda estiver na validade.
            </div>
          )}

          <BalanceSummary data={data} />

          <GrantForm
            tenantId={tenantId}
            onGranted={(text) => {
              setNotice({ text, tone: 'info' });
              reload();
            }}
          />

          <LotsTable
            lots={data.lots}
            currentOperatorId={currentOperatorId}
            canRevoke={isAdmin}
            onRevoke={(lot) => {
              setNotice(null);
              setRevoking(lot);
            }}
          />

          <PurchasesTable
            purchases={data.purchases}
            canRefund={isAdmin}
            onRefund={(purchase) => {
              setNotice(null);
              setRefunding(purchase);
            }}
          />

          <EntriesTable entries={data.entries} currentOperatorId={currentOperatorId} />
        </>
      )}

      {revoking && (
        <RevokeGrantDialog
          tenantId={tenantId}
          tradeName={tradeName}
          lot={revoking}
          onDone={(next) => {
            setRevoking(null);
            setNotice(next);
            reload();
          }}
          onCancel={() => setRevoking(null)}
        />
      )}

      {refunding && (
        <RefundPurchaseDialog
          tenantId={tenantId}
          tradeName={tradeName}
          purchase={refunding}
          onDone={(next) => {
            setRefunding(null);
            setNotice(next);
            reload();
          }}
          onCancel={() => setRefunding(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
//  Saldo
// ---------------------------------------------------------------------------

function BalanceSummary({ data }: { data: PlatformAiUnitsResponse }) {
  return (
    <div className="detail-grid" style={{ marginBottom: 12 }} data-testid="ai-units-balance">
      <div className="k">Cota do ciclo</div>
      <div data-testid="ai-units-cycle">
        {data.cycle ? (
          <>
            {formatUnits(data.cycle.used)} de {formatUnits(data.cycle.total)} usados
            <span className="muted">
              {' '}
              · ciclo desde {formatDate(data.cycle.startsAt)}, renova em {formatDate(data.cycle.endsAt)}
            </span>
          </>
        ) : (
          <span className="muted">Sem cota do plano no ciclo</span>
        )}
      </div>

      <div className="k">Pacotes e cortesias</div>
      <div data-testid="ai-units-packs">{unitsLabel(data.packUnits)} disponíveis</div>

      <div className="k">Carência</div>
      <div data-testid="ai-units-grace">
        {data.graceDebt > 0 ? (
          <>
            {unitsLabel(data.graceDebt)} de carência ainda não {data.graceDebt === 1 ? 'descontado' : 'descontados'}{' '}
            <span className="muted">(sai do próximo lote concedido)</span>
          </>
        ) : (
          <span className="muted">Nenhuma pendente</span>
        )}
      </div>

      <div className="k">Próximo vencimento</div>
      <div>
        {data.nextExpiry ? (
          <>
            {unitsLabel(data.nextExpiry.units)} em {formatDateTime(data.nextExpiry.at)}
          </>
        ) : (
          <span className="muted">—</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Cortesia
// ---------------------------------------------------------------------------

function GrantForm({ tenantId, onGranted }: { tenantId: string; onGranted: (message: string) => void }) {
  const [open, setOpen] = useState(false);
  const [units, setUnits] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const minDay = saoPauloIsoDate(0);
  const maxDay = saoPauloIsoDate(GRANT_MAX_DAYS);
  const parsedUnits = parseWholeNumber(units);
  const trimmed = reason.trim();
  const unitsValid = parsedUnits !== null && parsedUnits >= GRANT_MIN_UNITS && parsedUnits <= GRANT_MAX_UNITS;
  const untilValid = until !== '' && until >= minDay && until <= maxDay;
  const reasonValid = trimmed.length >= CONFIRM_REASON_MIN && trimmed.length <= CONFIRM_REASON_MAX;
  const valid = unitsValid && untilValid && reasonValid;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || submitting || parsedUnits === null) return;
    const payload: GrantAiUnitsPayload = { units: parsedUnits, expiresAt: overrideEndsAtFor(until), reason: trimmed };
    const key = idempotency.keyFor(grantTenantAiUnitsUrl(tenantId), payload);
    setSubmitting(true);
    setFieldErrors({});
    setError(null);
    try {
      const lot = await grantTenantAiUnits(tenantId, payload, { idempotencyKey: key });
      idempotency.settle();
      setOpen(false);
      setUnits('');
      setUntil('');
      setReason('');
      const settled = lot.units - lot.remainingUnits;
      onGranted(
        `Cortesia de ${unitsLabel(lot.units)} concedida, valendo até ${overrideLastDay(lot.expiresAt)}.` +
          (settled > 0
            ? ` ${unitsLabel(settled)} de carência ${settled === 1 ? 'foi descontado' : 'foram descontados'} dela.`
            : ''),
      );
    } catch (err) {
      idempotency.settle(err);
      const byField = problemFieldErrors(err);
      if (Object.keys(byField).length > 0) {
        setFieldErrors(byField);
      } else {
        setError(describeError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const fieldError = (field: 'units' | 'expiresAt' | 'reason') =>
    fieldErrors[field]?.length ? (
      <div className="error-banner" role="alert" style={{ marginTop: 6, padding: '8px 12px' }} data-testid={`grant-error-${field}`}>
        {fieldErrors[field][0]}
      </div>
    ) : null;

  if (!open) {
    return (
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-sm" onClick={() => setOpen(true)}>
          <Gift size={15} /> Conceder atendimentos de cortesia
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} aria-label="Conceder atendimentos de cortesia" className="card" style={{ marginBottom: 12 }}>
      <div className="card-title" style={{ marginBottom: 8 }}>
        Conceder atendimentos de cortesia
      </div>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        A cortesia entra como um lote na carteira, consumido depois da cota do plano, e já desconta a carência pendente.
        O motivo é nota interna: fica na trilha de auditoria e o dono não o vê.
      </p>
      <div className="grid-2">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="ai-units-grant-units">Atendimentos</label>
          <input
            id="ai-units-grant-units"
            type="number"
            inputMode="numeric"
            min={GRANT_MIN_UNITS}
            max={GRANT_MAX_UNITS}
            step={1}
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            aria-invalid={fieldErrors.units ? true : undefined}
          />
          <div className="muted" style={{ fontSize: 12 }}>
            De 1 a 10.000.
          </div>
          {fieldError('units')}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="ai-units-grant-until">Vale até (inclusive)</label>
          <input
            id="ai-units-grant-until"
            type="date"
            min={minDay}
            max={maxDay}
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            aria-invalid={fieldErrors.expiresAt ? true : undefined}
          />
          <div className="muted" style={{ fontSize: 12 }}>
            De hoje a 365 dias, no calendário de São Paulo.
          </div>
          {fieldError('expiresAt')}
        </div>
        <div className="field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
          <label htmlFor="ai-units-grant-reason">Motivo</label>
          <textarea
            id="ai-units-grant-reason"
            rows={2}
            maxLength={CONFIRM_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ex.: piloto do atendente"
            aria-invalid={fieldErrors.reason ? true : undefined}
          />
          {fieldError('reason')}
        </div>
      </div>
      {error && (
        <div className="error-banner" role="alert" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}
      <div className="btn-row" style={{ marginTop: 12 }}>
        <button type="submit" className="btn btn-sm" disabled={!valid || submitting}>
          {submitting ? 'Concedendo…' : 'Conceder'}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={submitting}
          onClick={() => {
            setOpen(false);
            setError(null);
            setFieldErrors({});
          }}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
//  Lotes, compras e lançamentos
// ---------------------------------------------------------------------------

function LotsTable({
  lots,
  currentOperatorId,
  canRevoke,
  onRevoke,
}: {
  lots: PlatformAiUnitLot[];
  currentOperatorId: string | null;
  canRevoke: boolean;
  onRevoke: (lot: PlatformAiUnitLot) => void;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, margin: '4px 0 8px' }}>Lotes</div>
      {lots.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Nenhum lote na carteira.
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Lotes de atendimentos">
            <THead>
              <Tr>
                <Th>Origem</Th>
                <Th>Quantidade</Th>
                <Th>Restante</Th>
                <Th>Concedido em</Th>
                <Th>Vale até</Th>
                <Th>Situação</Th>
                <Th>Detalhe</Th>
                {canRevoke && <Th>Ações</Th>}
              </Tr>
            </THead>
            <TBody>
              {lots.map((lot) => {
                const situation = lotSituation(lot);
                return (
                  <Tr key={lot.lotId} data-testid={`ai-unit-lot-${lot.lotId}`}>
                    <Td heading>{lotSourceLabel(lot.source)}</Td>
                    <Td label="Quantidade">{formatUnits(lot.units)}</Td>
                    <Td label="Restante">{formatUnits(lot.remainingUnits)}</Td>
                    <Td label="Concedido em">{formatDateTime(lot.grantedAt)}</Td>
                    <Td label="Vale até">
                      <span title={`Vence em ${formatDateTime(lot.expiresAt)}`}>{overrideLastDay(lot.expiresAt)}</span>
                    </Td>
                    <Td label="Situação">
                      <Badge tone={situation.tone}>{situation.label}</Badge>
                      {lot.closedAt && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          em {formatDateTime(lot.closedAt)}
                        </div>
                      )}
                    </Td>
                    <Td label="Detalhe">
                      {lot.source === 'concession' ? (
                        <>
                          por {operatorLabel(lot.grantedByOperatorId, currentOperatorId)}
                          {lot.reason && <div className="muted" style={{ fontSize: 12 }}>{lot.reason}</div>}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </Td>
                    {canRevoke && (
                      <Td action>
                        {isRevocableConcession(lot) && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onRevoke(lot)}>
                            Estornar cortesia
                          </button>
                        )}
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

function PurchasesTable({
  purchases,
  canRefund,
  onRefund,
}: {
  purchases: PlatformAiUnitPurchase[];
  canRefund: boolean;
  onRefund: (purchase: PlatformAiUnitPurchase) => void;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 14, margin: '4px 0 8px' }}>Compras de pacotes</div>
      {purchases.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Nenhuma compra de pacote.
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Compras de pacotes">
            <THead>
              <Tr>
                <Th>Compra</Th>
                <Th>Pacote</Th>
                <Th>Valor</Th>
                <Th>Pagamento</Th>
                <Th>Situação</Th>
                <Th>Pago em</Th>
                <Th>Reembolso</Th>
                {canRefund && <Th>Ações</Th>}
              </Tr>
            </THead>
            <TBody>
              {purchases.map((purchase) => {
                const status = purchaseStatusBadge(purchase.status);
                const refund = refundSummary(purchase);
                return (
                  <Tr key={purchase.purchaseId} data-testid={`ai-unit-purchase-${purchase.purchaseId}`}>
                    <Td heading>{formatDateTime(purchase.createdAt)}</Td>
                    <Td label="Pacote">
                      {unitsLabel(purchase.units)}
                      <div className="muted mono" style={{ fontSize: 12 }}>
                        {purchase.packCode}
                      </div>
                    </Td>
                    <Td label="Valor">{formatBrl(purchase.amount)}</Td>
                    <Td label="Pagamento">{paymentMethodLabel(purchase.paymentMethod)}</Td>
                    <Td label="Situação">
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td label="Pago em">{formatDateTime(purchase.paidAt)}</Td>
                    <Td label="Reembolso">
                      <span data-testid="ai-unit-refund">{refund.text}</span>
                      {purchase.refund.gatewayStatus && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          Asaas: {purchase.refund.gatewayStatus}
                        </div>
                      )}
                    </Td>
                    {canRefund && (
                      <Td action>
                        {purchase.refund.eligible && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onRefund(purchase)}>
                            Reembolsar pacote
                          </button>
                        )}
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

function EntriesTable({
  entries,
  currentOperatorId,
}: {
  entries: PlatformAiUnitsResponse['entries'];
  currentOperatorId: string | null;
}) {
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, margin: '4px 0 8px' }}>Últimos lançamentos</div>
      {entries.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Nenhum lançamento na carteira.
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Últimos lançamentos de atendimentos">
            <THead>
              <Tr>
                <Th>Quando</Th>
                <Th>Lançamento</Th>
                <Th>Quantidade</Th>
                <Th>Origem</Th>
                <Th>Quem</Th>
              </Tr>
            </THead>
            <TBody>
              {entries.map((entry) => (
                <Tr key={entry.entryId}>
                  <Td heading>{formatDateTime(entry.occurredAt)}</Td>
                  <Td label="Lançamento">
                    {entry.description}
                    {entry.reason && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {entry.reason}
                      </div>
                    )}
                  </Td>
                  <Td label="Quantidade" className="mono">
                    {signedUnits(entry.units)}
                  </Td>
                  <Td label="Origem">{entry.isGrace ? 'Carência' : lotSourceLabel(entry.source)}</Td>
                  <Td label="Quem">
                    {entry.actorOperatorId ? (
                      operatorLabel(entry.actorOperatorId, currentOperatorId)
                    ) : (
                      <span className="muted">sistema</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Estorno de cortesia e reembolso de pacote (só Admin)
// ---------------------------------------------------------------------------

function RevokeGrantDialog({
  tenantId,
  tradeName,
  lot,
  onDone,
  onCancel,
}: {
  tenantId: string;
  tradeName: string;
  lot: PlatformAiUnitLot;
  onDone: (notice: Notice) => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  async function revoke(reason: string | null) {
    if (!reason) return;
    const key = idempotency.keyFor(revokeTenantAiUnitsGrantUrl(tenantId, lot.lotId), { reason });
    setBusy(true);
    setError(null);
    try {
      const result = await revokeTenantAiUnitsGrant(tenantId, lot.lotId, reason, { idempotencyKey: key });
      idempotency.settle();
      onDone(
        result.revoked
          ? {
              text:
                `Cortesia estornada: ${unitsLabel(result.revokedUnits)} saíram da carteira.` +
                (result.usedUnits > 0 ? ` Os ${formatUnits(result.usedUnits)} já usados continuam no extrato.` : ''),
              tone: 'info',
            }
          : { text: 'A cortesia já estava fechada ou vencida: nada mudou.', tone: 'warning' },
      );
    } catch (err) {
      idempotency.settle(err);
      if (problemStatus(err) === 404) {
        onDone({ text: 'Esta cortesia não foi encontrada neste tenant. A lista foi atualizada.', tone: 'error' });
        return;
      }
      setError(describeFieldError(err));
      setBusy(false);
    }
  }

  return (
    <ReinforcedConfirmDialog
      title="Estornar cortesia"
      description={
        <>
          <p style={{ margin: '0 0 8px' }}>
            Os <strong>{unitsLabel(lot.remainingUnits)}</strong> que sobram desta cortesia saem da carteira de{' '}
            <strong>{tradeName}</strong> agora, e o lote é fechado.
          </p>
          <p style={{ margin: 0 }}>
            O que já foi usado ({formatUnits(lot.units - lot.remainingUnits)} de {formatUnits(lot.units)}) continua no
            extrato. Para devolver atendimentos depois, conceda uma cortesia nova.
          </p>
        </>
      }
      acknowledgement={
        <>
          Entendo que os {unitsLabel(lot.remainingUnits)} saem da carteira de <strong>{tradeName}</strong> e não voltam
          sozinhos.
        </>
      }
      reasonLabel="Motivo do estorno"
      confirmLabel="Estornar"
      busy={busy}
      error={error}
      onConfirm={revoke}
      onCancel={onCancel}
    />
  );
}

function RefundPurchaseDialog({
  tenantId,
  tradeName,
  purchase,
  onDone,
  onCancel,
}: {
  tenantId: string;
  tradeName: string;
  purchase: PlatformAiUnitPurchase;
  onDone: (notice: Notice) => void;
  onCancel: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  /**
   * O reembolso com o código do step-up (o reembolso devolve dinheiro e não se desfaz: a API pede o
   * TOTP antes da `Idempotency-Key`). O motivo é o confirmado na confirmação reforçada, congelado
   * pelo `useStepUpDialog`; a recusa de step-up deixa o modal do código aberto e repete com a
   * mesma chave.
   */
  async function refund(proof: StepUpProof, reason: string) {
    const key = idempotency.keyFor(refundTenantAiUnitPurchaseUrl(tenantId, purchase.purchaseId), { reason });
    let result;
    try {
      result = await refundTenantAiUnitPurchase(tenantId, purchase.purchaseId, reason, {
        idempotencyKey: key,
        ...stepUpOptions(proof),
      });
      idempotency.settle();
    } catch (err) {
      idempotency.settle(err);
      throw err;
    }
    onDone(
      result.pendingAtGateway
        ? {
            text:
              'Pacote estornado. O Asaas ainda não confirmou o reembolso: a compra fica como reembolso pendente no ' +
              'gateway, e o sistema tenta de novo até a confirmação.',
            tone: 'warning',
          }
        : {
            text: `Reembolso concluído: o pacote foi estornado e o Asaas confirmou a devolução de ${formatBrl(purchase.amount)}.`,
            tone: 'info',
          },
    );
  }

  /** O que não é recusa de step-up fecha o modal do código e volta para cá. */
  function onFailure(err: unknown) {
    const refusal = refundRefusalOf(err);
    if (problemErrorCode(err) === 'billing.ai_units_refund_not_allowed') {
      onDone({ text: `Reembolso recusado: ${refundRefusalLabel(refusal)}`, tone: 'error' });
      return;
    }
    if (problemErrorCode(err) === 'billing.ai_units_purchase_not_found') {
      onDone({ text: 'Esta compra não foi encontrada neste tenant. A lista foi atualizada.', tone: 'error' });
      return;
    }
    setError(describeFieldError(err));
  }

  const stepUp = useStepUpDialog<string>(refund, onFailure, {
    // Passkey (C14): o desafio preso a esta compra.
    passkeyTarget: () => stepUpTarget('aiUnitPurchaseRefund', purchase.purchaseId),
  });

  if (stepUp.open) {
    return (
      <StepUpDialog
        title="Reembolsar pacote"
        description={
          <>
            <p style={{ margin: '0 0 8px' }}>
              O Asaas devolve <strong>{formatBrl(purchase.amount)}</strong> a <strong>{tradeName}</strong> e o pacote de{' '}
              <strong>{unitsLabel(purchase.units)}</strong> sai da carteira. O reembolso não se desfaz.
            </p>
            <p style={{ margin: 0 }}>Confirme com o código atual do seu autenticador.</p>
          </>
        }
        confirmLabel="Reembolsar"
        busy={stepUp.busy}
        error={stepUp.error}
        onSubmit={stepUp.submit}
        onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        onCancel={() => {
          if (stepUp.busy) return;
          stepUp.cancel();
          onCancel();
        }}
      />
    );
  }

  return (
    <ReinforcedConfirmDialog
      title="Reembolsar pacote"
      description={
        <>
          <p style={{ margin: '0 0 8px' }}>
            O pacote de <strong>{unitsLabel(purchase.units)}</strong> sai da carteira de <strong>{tradeName}</strong>{' '}
            agora, e o Asaas devolve <strong>{formatBrl(purchase.amount)}</strong> pela forma de pagamento da compra (
            {paymentMethodLabel(purchase.paymentMethod)}).
          </p>
          <p style={{ margin: 0 }}>
            Se o Asaas não confirmar na hora, o pacote já fica estornado e o reembolso segue pendente no gateway até a
            confirmação. Depois desta confirmação, o código do autenticador é pedido.
          </p>
        </>
      }
      acknowledgement={
        <>
          Entendo que o pacote sai da carteira de <strong>{tradeName}</strong> e que o reembolso não se desfaz.
        </>
      }
      reasonLabel="Motivo do reembolso"
      confirmLabel="Reembolsar"
      error={error}
      onConfirm={(reason) => {
        if (!reason) return;
        setError(null);
        stepUp.start(reason);
      }}
      onCancel={onCancel}
    />
  );
}
